import { toRef, MaybeRefOrGetter } from "@vueuse/core";
import { watch } from "vue";

import { getClient } from "./client";
import { useAsyncState } from "@vueuse/core";

export type ItemCostCategory =
  | "engineering_and_design"
  | "utility_interconnection"
  | "permitting"
  | "materials"
  | "mechanical_installation_labor"
  | "electrical_installation_labor"
  | "sales_tax"
  | "general_conditions"
  | "station_a_fee"
  | "other";

export type TechCostCategory =
  | "rooftop_solar"
  | "carport_solar"
  | "groundmount_solar"
  | "floating_solar"
  | "battery_system"
  | "ev_charging";

export type CostCategory = ItemCostCategory | TechCostCategory;

export type Other = "other_cost_description" | "totals_synced";

export type CostAPI = {
  form_response_id: string;
  cost_category: CostCategory;
  cost: number;
  created: string;
  updated: string;
};

type CostsAPIResponse = Record<CostCategory, CostAPI>;

export type Cost = {
  formResponseId: string;
  costCategory: CostCategory;
  cost: number;
  created: Date;
  updated: Date;
};

export type Costs = Cost[];

export type TechnologyChoiceSet = {
  rooftop_solar: boolean;
  carport_solar: boolean;
  groundmount_solar: boolean;
  floating_solar: boolean;
  battery_system: boolean;
  ev_charging: boolean;
};

export type TechnologyChoiceAPI = {
  form_response_id: string;
  created: string;
  updated: string;
  technology_choice: TechnologyChoiceSet;
};

export type OtherCostDescriptionAPI = {
  form_response_id: string;
  other_cost_description: string;
  created: string;
  updated: string;
};

export type OtherCostDescription = {
  formResponseId: string;
  otherCostDescription: string;
  created: Date;
  updated: Date;
};

// Data model for what comes back from the API
export type AllCashAPIResponse = {
  // form response ID
  id: string;
  proposal_id: string;
  form_id: string;
  created: string;
  updated: string;
  costs: CostsAPIResponse;
  technology_choice: TechnologyChoiceAPI;
  other_cost_description: OtherCostDescriptionAPI;
};

// Data model for what the function below sends back to the
// All Cash component (and is reactive)
export type AllCash = {
  // form response ID
  id: string;
  proposalId: string;
  formId: string;
  created: Date;
  updated: Date;
  // Turn the CostsAPIResponse dictionary structure into a list of costs
  // to make it easy to maintain reactivity and access in the AllCashForm component
  costs: Costs;
  technologyChoice?: TechnologyChoiceSet;
  otherCostDescription?: OtherCostDescription;
};

// Add up all the types of responses in the All Cash Form - useful for tracking errors
export type AllCashCostField = ItemCostCategory | Other | TechCostCategory;

// Mapping from cost categories to display name of field
export const ItemCostCategoryDict: Record<ItemCostCategory, string> = {
  engineering_and_design: "Engineering and design",
  utility_interconnection: "Utility interconnection costs",
  permitting: "Permitting",
  materials: "Materials",
  mechanical_installation_labor: "Mechanical installation labor",
  electrical_installation_labor: "Electrical installation labor",
  sales_tax: "Sales tax",
  general_conditions: "General conditions",
  station_a_fee: "Station A fee",
  other: "Other",
};

// Mapping cost categories to their tooltip description
export const ItemCostDescriptionDict: Record<ItemCostCategory, string> = {
  engineering_and_design:
    "All costs related to the engineering and design of the project.",
  utility_interconnection:
    "Costs of the interconnection application fee, estimated utility-side upgrades, and any electrical upgrades required to interconnect the system.",
  permitting: "Costs for acquiring necessary permits.",
  materials:
    "Costs for solar panels, inverters, racking, BOS, and related equipment.",
  mechanical_installation_labor:
    "Labor costs for physically mounting solar panels and structural components.",
  electrical_installation_labor:
    "Costs for wiring, connecting, and testing the electrical components of the system.",
  sales_tax: "Tax applied to the purchase of materials for the project.",
  general_conditions:
    "Overhead expenses like project management, site security, and temporary facilities.",
  station_a_fee:
    "Total fee paid to Station A for a successful match for the project, as described in the disclosures (if applicable).",
  other:
    "Any other project costs not otherwise categorized (this is usually $0).",
};

export const TechCostCategoryDict: Record<TechCostCategory, string> = {
  rooftop_solar: "Rooftop solar",
  carport_solar: "Carport solar",
  groundmount_solar: "Groundmount solar",
  floating_solar: "Floating solar",
  battery_system: "Battery system",
  ev_charging: "EV charging",
};

export function useAllCash(
  proposalId: MaybeRefOrGetter<string | undefined>,
  formResponseId: MaybeRefOrGetter<string | undefined>,
) {
  const reactiveProposalId = toRef<string | undefined>(proposalId);
  const reactiveFormResponseId = toRef<string | undefined>(formResponseId);
  const client = getClient();

  const {
    state: allCashState,
    isReady: allCashReady,
    isLoading: allCashLoading,
    error: allCashError,
    execute: reloadAllCash,
  } = useAsyncState<AllCash | null>(
    async () => {
      if (reactiveProposalId.value && reactiveFormResponseId.value) {
        const res = await client.request<AllCashAPIResponse>({
          url: `/proposals/${encodeURIComponent(
            reactiveProposalId.value,
          )}/form_responses/${encodeURIComponent(
            reactiveFormResponseId.value,
          )}/allcash`,
        });

        const costs: Costs = [];
        for (const cost_record of Object.values(res.data.costs)) {
          costs.push({
            formResponseId: cost_record.form_response_id,
            costCategory: cost_record.cost_category,
            cost: cost_record.cost,
            created: new Date(cost_record.created),
            updated: new Date(cost_record.updated),
          });
        }

        let otherCostDescription: OtherCostDescription | undefined;
        if (res.data.other_cost_description !== null) {
          otherCostDescription = {
            formResponseId: res.data.other_cost_description.form_response_id,
            created: new Date(res.data.other_cost_description.created),
            updated: new Date(res.data.other_cost_description.updated),
            otherCostDescription:
              res.data.other_cost_description.other_cost_description,
          };
        }

        let technologyChoice: TechnologyChoiceSet | undefined = undefined;
        if (res.data.technology_choice != null) {
          technologyChoice = res.data.technology_choice.technology_choice;
        }

        return {
          id: res.data.id,
          proposalId: res.data.proposal_id,
          formId: res.data.form_id,
          created: new Date(res.data.created),
          updated: new Date(res.data.updated),
          costs,
          technologyChoice,
          otherCostDescription,
        };
      } else {
        return null;
      }
    },
    null,
    { resetOnExecute: false },
  );

  async function updateCost(
    costCategory: CostCategory,
    cost: number,
    shouldGenerateComment: boolean,
  ) {
    if (reactiveProposalId.value && reactiveFormResponseId.value) {
      const res = await client.post<AllCashAPIResponse>(
        `/proposals/${encodeURIComponent(
          reactiveProposalId.value,
        )}/form_responses/${encodeURIComponent(
          reactiveFormResponseId.value,
        )}/allcash/costs/${encodeURIComponent(costCategory)}`,
        undefined,
        {
          params: {
            cost: cost,
            generate_comment: shouldGenerateComment,
          },
        },
      );
      if (res.status !== 200) {
        throw new Error(
          `Update Cost failed for cost category: ${costCategory} with cost: ${cost}`,
        );
      }
    }
  }

  async function deleteCost(
    costCategory: CostCategory,
    shouldGenerateComment: boolean,
  ) {
    if (reactiveProposalId.value && reactiveFormResponseId.value) {
      const res = await client.delete<AllCashAPIResponse>(
        `/proposals/${encodeURIComponent(
          reactiveProposalId.value,
        )}/form_responses/${encodeURIComponent(
          reactiveFormResponseId.value,
        )}/allcash/costs/${encodeURIComponent(costCategory)}`,
        {
          params: {
            generate_comment: shouldGenerateComment,
          },
        },
      );
      if (res.status !== 200) {
        throw new Error(
          `Delete Cost failed for cost category: ${costCategory}`,
        );
      }
    }
  }

  async function updateOtherCostDescription(
    otherCostDescription: string,
    shouldGenerateComment: boolean,
  ) {
    if (reactiveProposalId.value && reactiveFormResponseId.value) {
      const res = await client.post<AllCashAPIResponse>(
        `/proposals/${encodeURIComponent(
          reactiveProposalId.value,
        )}/form_responses/${encodeURIComponent(
          reactiveFormResponseId.value,
        )}/allcash/other_cost_description`,
        undefined,
        {
          params: {
            other_cost_description: otherCostDescription,
            generate_comment: shouldGenerateComment,
          },
        },
      );
      if (res.status !== 200) {
        throw new Error(
          `Update Other Cost description failed for description: ${otherCostDescription}`,
        );
      }
    }
  }

  async function deleteOtherCostDescription(
    otherCostDescription: string | undefined,
    shouldGenerateComment: boolean,
  ) {
    if (reactiveProposalId.value && reactiveFormResponseId.value) {
      const res = await client.delete<AllCashAPIResponse>(
        `/proposals/${encodeURIComponent(
          reactiveProposalId.value,
        )}/form_responses/${encodeURIComponent(
          reactiveFormResponseId.value,
        )}/allcash/other_cost_description`,
        {
          params: {
            generate_comment: shouldGenerateComment,
          },
        },
      );
      if (res.status !== 200) {
        throw new Error(
          `Delete failed for other cost description: ${otherCostDescription}`,
        );
      }
    }
  }

  async function updateTechnologies(
    techChoices: TechnologyChoiceSet,
    shouldGenerateComment: boolean,
  ) {
    if (reactiveProposalId.value && reactiveFormResponseId.value) {
      const res = await client.post<AllCashAPIResponse>(
        `/proposals/${encodeURIComponent(
          reactiveProposalId.value,
        )}/form_responses/${encodeURIComponent(
          reactiveFormResponseId.value,
        )}/allcash/technologies`,
        techChoices,
        {
          params: {
            generate_comment: shouldGenerateComment,
          },
        },
      );
      if (res.status !== 200) {
        throw new Error(
          `Update technologies failed for technology choice set: ${techChoices}`,
        );
      }
      const costs: Costs = [];
      for (const cost_record of Object.values(res.data.costs)) {
        costs.push({
          formResponseId: cost_record.form_response_id,
          costCategory: cost_record.cost_category,
          cost: cost_record.cost,
          created: new Date(cost_record.created),
          updated: new Date(cost_record.updated),
        });
      }

      let otherCostDescription: OtherCostDescription | undefined;
      if (res.data.other_cost_description !== null) {
        otherCostDescription = {
          formResponseId: res.data.other_cost_description.form_response_id,
          created: new Date(res.data.other_cost_description.created),
          updated: new Date(res.data.other_cost_description.updated),
          otherCostDescription:
            res.data.other_cost_description.other_cost_description,
        };
      }

      let technologyChoice: TechnologyChoiceSet | undefined = undefined;
      if (res.data.technology_choice != null) {
        technologyChoice = res.data.technology_choice.technology_choice;
      }

      return {
        id: res.data.id,
        proposalId: res.data.proposal_id,
        formId: res.data.form_id,
        created: new Date(res.data.created),
        updated: new Date(res.data.updated),
        costs,
        technologyChoice,
        otherCostDescription,
      };
    }
  }

  watch([reactiveProposalId, reactiveFormResponseId], () => reloadAllCash());

  return {
    allCashState,
    allCashReady,
    allCashLoading,
    allCashError,
    reloadAllCash,
    updateCost,
    deleteCost,
    updateOtherCostDescription,
    deleteOtherCostDescription,
    updateTechnologies,
  };
}
